import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  QrCode2,
  Add,
  Delete,
  CameraAlt,
  Publish,
  Inventory2,
  CheckCircle,
} from '@mui/icons-material';
import apiService from '../services/api.service';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import ScannerView from '../components/warehouse/ScannerView';
import type { ItemBarcodeRow, ItemUom, StagedBarcode, BarcodeSubmitResult } from '../types/warehouse';

type Toast = { severity: 'success' | 'info' | 'warning' | 'error'; message: string } | null;
interface ItemOption {
  itemCode: string;
  itemName: string;
  itemGroupName?: string;
}

const localId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** Human label for a UoM option (e.g. "EA" or "EA — Each"). */
const uomLabel = (u: ItemUom): string => (u.name && u.name !== u.code ? `${u.code} — ${u.name}` : u.code);

/**
 * Warehouse → Barcode: an item-first companion to scanning. Pick an item, see the
 * barcodes already linked to it, then register new ones (typed or camera-scanned)
 * for many items in one session. On submit each new barcode is written to SAP B1
 * (OBCD) and pulled straight back into the local cache — the same
 * write-to-SAP-then-sync-back contract used by the GRPO submit.
 */
export default function WarehouseBarcode(): JSX.Element {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || ''), []);

  const [itemQuery, setItemQuery] = useState('');
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [itemLoading, setItemLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemOption | null>(null);

  const [existing, setExisting] = useState<ItemBarcodeRow[]>([]);
  const [existingLoading, setExistingLoading] = useState(false);

  const [uomOptions, setUomOptions] = useState<ItemUom[]>([]);
  const [uomEntry, setUomEntry] = useState<number | ''>('');
  const [uomLoading, setUomLoading] = useState(false);

  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanOpen, setScanOpen] = useState(false);

  const [staged, setStaged] = useState<StagedBarcode[]>([]);
  const [results, setResults] = useState<BarcodeSubmitResult[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  // -------- item search (debounced) --------
  useEffect(() => {
    const q = itemQuery.trim();
    let cancelled = false;
    setItemLoading(true);
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.searchWarehouseItems(companyId, q || undefined);
        if (!cancelled) setItemOptions(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (!cancelled) setItemOptions([]);
      } finally {
        if (!cancelled) setItemLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemQuery, companyId]);

  // -------- existing barcodes for the selected item --------
  const loadExisting = useCallback(
    async (itemCode: string) => {
      setExistingLoading(true);
      try {
        const res: any = await apiService.getItemBarcodes(companyId, itemCode);
        setExisting(Array.isArray(res?.data) ? (res.data as ItemBarcodeRow[]) : []);
      } catch {
        setExisting([]);
      } finally {
        setExistingLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (selectedItem) void loadExisting(selectedItem.itemCode);
    else setExisting([]);
  }, [selectedItem, loadExisting]);

  // -------- UoM options for the selected item (a barcode must map to one UoM) --------
  useEffect(() => {
    if (!selectedItem) {
      setUomOptions([]);
      setUomEntry('');
      return;
    }
    let cancelled = false;
    setUomLoading(true);
    apiService
      .getItemUoms(companyId, selectedItem.itemCode)
      .then((res: any) => {
        if (cancelled) return;
        const data = res?.data ?? {};
        const uoms: ItemUom[] = Array.isArray(data.uoms) ? data.uoms : [];
        setUomOptions(uoms);
        const def = typeof data.defaultEntry === 'number' ? data.defaultEntry : uoms.find((u) => u.isDefault)?.entry;
        setUomEntry(def ?? (uoms[0]?.entry ?? ''));
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to "Manual" UoM so the user is never blocked.
        setUomOptions([{ entry: -1, code: 'Manual', name: 'Manual', isDefault: true }]);
        setUomEntry(-1);
      })
      .finally(() => {
        if (!cancelled) setUomLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedItem, companyId]);

  // UoM entry → label, for showing the UoM of existing barcodes.
  const uomLabelByEntry = useMemo(() => {
    const m = new Map<number, string>();
    for (const u of uomOptions) m.set(u.entry, uomLabel(u));
    return m;
  }, [uomOptions]);

  // -------- add a barcode to the staging list --------
  const addBarcode = useCallback(async () => {
    if (!selectedItem) {
      setToast({ severity: 'warning', message: 'Choose an item first.' });
      return;
    }
    const code = barcodeInput.trim();
    if (!code) return;

    // Already linked to this item (synced or pending), or already staged → no-op.
    if (existing.some((b) => b.barcode === code)) {
      setToast({ severity: 'info', message: `Barcode is already linked to ${selectedItem.itemCode}.` });
      return;
    }
    if (staged.some((s) => s.barcode === code && s.itemCode === selectedItem.itemCode)) {
      setToast({ severity: 'info', message: 'Barcode is already in the list below.' });
      return;
    }

    // Uniqueness: reject a code already owned by another item (local check; the
    // submit re-validates against live SAP).
    try {
      const res: any = await apiService.getBarcodeOwner(companyId, code);
      const owner = res?.data?.owner;
      if (owner && owner.itemCode && owner.itemCode !== selectedItem.itemCode) {
        setToast({ severity: 'error', message: `Barcode is already used by item ${owner.itemCode}.` });
        return;
      }
    } catch {
      /* non-fatal — submit will do the authoritative check */
    }
    if (staged.some((s) => s.barcode === code)) {
      setToast({ severity: 'error', message: 'Barcode is already staged for another item below.' });
      return;
    }
    if (uomEntry === '') {
      setToast({ severity: 'warning', message: 'Choose a unit of measure for this barcode.' });
      return;
    }

    const chosen = uomOptions.find((u) => u.entry === uomEntry);
    setStaged((prev) => [
      {
        id: localId(),
        itemCode: selectedItem.itemCode,
        itemName: selectedItem.itemName,
        barcode: code,
        uomEntry: uomEntry as number,
        uomLabel: chosen ? uomLabel(chosen) : String(uomEntry),
      },
      ...prev,
    ]);
    setBarcodeInput('');
  }, [selectedItem, barcodeInput, existing, staged, companyId, uomEntry, uomOptions]);

  const removeStaged = useCallback((id: string) => {
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // -------- submit the whole session to SAP --------
  const submit = useCallback(async () => {
    if (staged.length === 0) return;
    setSubmitting(true);
    setResults([]);
    try {
      const entries = staged.map((s) => ({ itemCode: s.itemCode, barcode: s.barcode, uomEntry: s.uomEntry }));
      const res: any = await apiService.submitItemBarcodes(companyId, entries);
      const rows: BarcodeSubmitResult[] = Array.isArray(res?.data) ? res.data : [];
      setResults(rows);

      // Keep only rows that need attention (conflict/error); clear posted ones.
      const failedKeys = new Set(
        rows.filter((r) => r.status === 'conflict' || r.status === 'error').map((r) => `${r.itemCode}|${r.barcode}`)
      );
      setStaged((prev) => prev.filter((s) => failedKeys.has(`${s.itemCode}|${s.barcode}`)));

      const m = res?.meta ?? {};
      const okCount = (m.created ?? 0) + (m.exists ?? 0);
      const bad = (m.conflict ?? 0) + (m.error ?? 0);
      setToast({
        severity: bad > 0 ? 'warning' : 'success',
        message:
          bad > 0
            ? `${okCount} barcode(s) saved to SAP; ${bad} need attention (see below).`
            : `${okCount} barcode(s) saved to SAP and synced. They resolve immediately now.`,
      });

      // The backend already synced new links back into the cache — refresh the view.
      if (selectedItem) void loadExisting(selectedItem.itemCode);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Barcode submission failed.' });
    } finally {
      setSubmitting(false);
    }
  }, [staged, companyId, selectedItem, loadExisting]);

  const stagedColumns: ResponsiveTableColumn<StagedBarcode>[] = useMemo(
    () => [
      { id: 'item', header: 'Item', cell: (r) => <strong>{r.itemCode}</strong> },
      { id: 'name', header: 'Description', cell: (r) => r.itemName || '—', hideOnMobile: true },
      { id: 'barcode', header: 'New barcode', cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.barcode}</span> },
      { id: 'uom', header: 'UoM', cell: (r) => r.uomLabel || '—' },
      {
        id: 'actions',
        header: '',
        align: 'right',
        cell: (r) => (
          <Tooltip title="Remove">
            <IconButton size="small" color="error" onClick={() => removeStaged(r.id)} aria-label="Remove">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [removeStaged]
  );

  const resultColor = (s: BarcodeSubmitResult['status']): 'success' | 'info' | 'error' =>
    s === 'created' ? 'success' : s === 'exists' ? 'info' : 'error';

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Select a company first to maintain barcodes.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <QrCode2 color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Barcode
        </Typography>
        {staged.length > 0 && <Chip size="small" color="primary" label={`${staged.length} pending`} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Link barcodes to items in SAP B1. Pick an item, add one or more barcodes (type or scan), repeat for as many
        items as you need, then submit. Each barcode must be unique — a code already used by another item is rejected.
      </Typography>

      {/* --- Item + add-barcode panel --- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Autocomplete<ItemOption>
          options={itemOptions}
          loading={itemLoading}
          value={selectedItem}
          onChange={(_e, v) => setSelectedItem(v)}
          onInputChange={(_e, v, reason) => {
            if (reason === 'input') setItemQuery(v);
          }}
          getOptionLabel={(o) => `${o.itemCode} — ${o.itemName}`}
          isOptionEqualToValue={(a, b) => a.itemCode === b.itemCode}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Item"
              placeholder="Search by item code or name…"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <Inventory2 fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: (
                  <>
                    {itemLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedItem && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Existing barcodes for {selectedItem.itemCode}
              {existingLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Typography>
            {existing.length === 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {existingLoading ? 'Loading…' : 'No barcode is defined for this item yet. Add one below.'}
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {existing.map((b) => {
                  const uom = b.uomEntry != null ? uomLabelByEntry.get(b.uomEntry) : undefined;
                  return (
                    <Chip
                      key={b.barcode}
                      label={uom ? `${b.barcode} · ${uom}` : b.barcode}
                      size="small"
                      color={b.source === 'pending' ? 'warning' : 'default'}
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                      title={b.source === 'pending' ? 'Captured locally, not yet in SAP' : 'In SAP'}
                    />
                  );
                })}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                label="New barcode"
                placeholder="Type the barcode, or scan it"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addBarcode();
                }}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              <TextField
                select
                size="small"
                label="UoM"
                value={uomEntry === '' ? '' : String(uomEntry)}
                onChange={(e) => setUomEntry(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={uomLoading || uomOptions.length === 0}
                helperText={uomLoading ? 'Loading…' : uomOptions.length <= 1 ? undefined : 'Default is the item UoM'}
                sx={{ minWidth: 160 }}
              >
                {uomOptions.map((u) => (
                  <MenuItem key={u.entry} value={String(u.entry)}>
                    {uomLabel(u)}
                    {u.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" startIcon={<CameraAlt />} onClick={() => setScanOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                Scan
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => void addBarcode()}
                disabled={!barcodeInput.trim() || uomEntry === ''}
              >
                Add
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* --- Staged list + submit --- */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Barcodes to add ({staged.length})
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={16} /> : <Publish />}
            onClick={() => void submit()}
            disabled={submitting || staged.length === 0}
          >
            {submitting ? 'Submitting…' : 'Submit to SAP B1'}
          </Button>
        </Stack>
        <ResponsiveTable
          ariaLabel="Barcodes to add"
          columns={stagedColumns}
          rows={staged}
          getRowKey={(r) => r.id}
          emptyMessage="Nothing staged yet. Pick an item above and add barcodes."
          size="small"
        />

        {results.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Last submission result
            </Typography>
            <Stack spacing={0.75}>
              {results.map((r, i) => (
                <Stack key={`${r.itemCode}-${r.barcode}-${i}`} direction="row" spacing={1} alignItems="center">
                  {r.status === 'created' || r.status === 'exists' ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Chip size="small" color="error" label="!" sx={{ minWidth: 24 }} />
                  )}
                  <Chip size="small" color={resultColor(r.status)} label={r.status} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {r.barcode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    → {r.itemCode}
                    {r.message ? ` · ${r.message}` : ''}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* --- Camera scan dialog --- */}
      <ResponsiveDialog open={scanOpen} onClose={() => setScanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scan barcode</DialogTitle>
        <DialogContent dividers>
          <ScannerView
            prompt="Point the camera at the barcode. The decoded value drops into the field — review it, then Add."
            onDecode={(text) => {
              setBarcodeInput(text.trim());
              setScanOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanOpen(false)}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
